import { doNotification } from "../../../components/toast/nofitication";
import { handleError } from "../../../helperFunctions";
import { BaseAPIResponse, QueryProps } from "../../../utils/types";
import { baseApi } from "../../api";
import { AuthSliceActions } from "../auth/auth.slice";
import {
  ApprovalSummaryQuery,
  ApprovalSummaryResponse,
  AssignOfficialPayload,
  CooperativeMembershipForm,
  CooperativeOfficialsResponse,
  GetCooperativeDepositConfig,
  GetCooperativeInvitationResponse,
  GetCooperativeSharesConfig,
  GetCooprativeMembershipFormDetailsResponse,
  GetDefaultCooprativeMembershipFormDetailsResponse,
  GetMembershipFormFieldTypesResponse,
  JoinCooperativePayload,
  MembershipPaymentResponse,
  MembershipProfileQuery,
  MembershipProfileResponse,
  SingleCoopApprovalSummaryResponse,
  SingleCooperativeDetailsResponse,
  UpdateDepositsConfig,
  UpdateSharesConfig,
  UserCooperativesResponse,
} from "./cooperative.type";

export const cooperativeApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    fetchInvitations: builder.query<GetCooperativeInvitationResponse, void>({
      query: () => {
        return {
          url: `/Settings/invitation`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    fetchAllApprovalsSummary: builder.query<
      ApprovalSummaryResponse,
      ApprovalSummaryQuery
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Manage/Approval/status`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            AuthSliceActions.officialStatustUpdated(!!data?.data?.Official),
          );

          if (data?.data?.Official) {
            dispatch(
              AuthSliceActions.approvalCountUpdated(
                data.data.totalapprovalCount,
              ),
            );
          }
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    fetchSingleCoopApprovalsSummary: builder.query<
      SingleCoopApprovalSummaryResponse,
      {
        coop: string;
      }
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/approval/status`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    toggleAcceptOfficialInvite: builder.mutation<
      BaseAPIResponse,
      {
        coop: number;
        isAccept: boolean;
      }
    >({
      query: (payload) => {
        return {
          url: `/Cooperative/Register/Official/Member/AR`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification(
              `Invitation ${
                arg?.isAccept ? "accepted" : "declined"
              } sucessfully`,
              "success",
            );
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    rejectMembershipInvite: builder.mutation<
      BaseAPIResponse,
      {
        coop: number;
        answer: boolean;
      }
    >({
      query: (payload) => {
        return {
          url: `/Membership/Invitation/link/decline`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Invitation rejected sucessfully", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    uploadCooperativeLogo: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Cooperative/Logo`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateCooperativeDescription: builder.mutation<
      BaseAPIResponse,
      {
        desc: string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Cooperative/Description`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Description updated successfully", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    uploadCooperativeByeLaws: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Cooperative/Bye/Law`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Document uploaded successfully", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    uploadCooperativeCertificate: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Cooperative/Certificate`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Document uploaded successfully", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getMembershipFormFieldTypes: builder.query<
      GetMembershipFormFieldTypesResponse,
      void
    >({
      query: () => {
        return {
          url: `/Settings/default/FormType`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getDefaultMembershipForm: builder.query<
      GetDefaultCooprativeMembershipFormDetailsResponse,
      void
    >({
      query: () => {
        return {
          url: `/Settings/default/FormField`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getMembershipForm: builder.query<
      GetCooprativeMembershipFormDetailsResponse,
      void
    >({
      query: () => {
        return {
          url: `/Membership/FormDetail`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    updateMembershipform: builder.mutation<
      BaseAPIResponse,
      CooperativeMembershipForm
    >({
      query: (payload) => {
        return {
          url: `/Membership/FormDetail`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Membership form updated successfully", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getCooperativeDepositsConfig: builder.query<
      GetCooperativeDepositConfig,
      void
    >({
      query: () => {
        return {
          url: `/Depository/Settings`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getCooperativeSharesConfig: builder.query<GetCooperativeSharesConfig, void>(
      {
        query: () => {
          return {
            url: `/Shares/Settings`,
            method: "GET",
          };
        },
        async onQueryStarted(arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            handleError(err, false);
          }
        },
      },
    ),

    getAllCooperativeConfig: builder.query<GetCooperativeSharesConfig, void>({
      query: () => {
        return {
          url: `/Cooperative/Config/Setting`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    updateDepositsConfig: builder.mutation<
      BaseAPIResponse,
      UpdateDepositsConfig
    >({
      query: (payload) => {
        return {
          url: `/Depository/Settings`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Deposit configurations saved", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    updateSharesConfig: builder.mutation<BaseAPIResponse, UpdateSharesConfig>({
      query: (payload) => {
        return {
          url: `/Shares/Settings`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;

          if (resp.data.success) {
            doNotification("Shares configurations saved", "success");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getCooperativeOfficials: builder.query<CooperativeOfficialsResponse, void>({
      query: () => {
        return {
          url: `/Cooperative/Register/Official`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    assignCooperativeOfficial: builder.mutation<
      BaseAPIResponse,
      AssignOfficialPayload
    >({
      query: (payload) => {
        return {
          url: `/Cooperative/Register/Official`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    removeCooperativeOfficial: builder.mutation<
      BaseAPIResponse,
      {
        official: number | string;
      }
    >({
      query: (payload) => {
        return {
          url: `/Cooperative/Register/Official/delete`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getMembershipProfile: builder.query<
      MembershipProfileResponse,
      MembershipProfileQuery
    >({
      query: (params) => {
        return {
          url: `/Membership/Detail`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getSingleCooperativeDetails: builder.query<
      SingleCooperativeDetailsResponse,
      { id: string }
    >({
      query: (params) => {
        return {
          url: `/Cooperative/detail`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    joinCooperative: builder.mutation<BaseAPIResponse, JoinCooperativePayload>({
      query: (payload) => {
        return {
          url: `/Membership/FormDetail/filled`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getMembershipPaymentInfo: builder.mutation<
      MembershipPaymentResponse,
      {
        coop: string | number;
        callbackurl?: string;
      }
    >({
      query: (params) => {
        return {
          url: `/Payment/Paystack/View/Registration`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    verifyMembershipPaymentInfo: builder.mutation<
      BaseAPIResponse,
      {
        coop: number;
        trxref: string;
        reference: string;
        regPaymentId: number;
      }
    >({
      query: (payload) => {
        return {
          url: `/Payment/Paystack/View/Registration`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getUserCooperatives: builder.query<UserCooperativesResponse, QueryProps>({
      query: (params) => {
        return {
          url: `/Cooperative/my`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),
  }),
});

export const {
  useFetchInvitationsQuery,
  useFetchAllApprovalsSummaryQuery,
  useToggleAcceptOfficialInviteMutation,
  useRejectMembershipInviteMutation,
  useUploadCooperativeLogoMutation,
  useUpdateCooperativeDescriptionMutation,
  useGetMembershipFormFieldTypesQuery,
  useGetDefaultMembershipFormQuery,
  useGetMembershipFormQuery,
  useUpdateMembershipformMutation,
  useGetCooperativeDepositsConfigQuery,
  useGetCooperativeSharesConfigQuery,
  useGetAllCooperativeConfigQuery,
  useUpdateDepositsConfigMutation,
  useUpdateSharesConfigMutation,
  useGetCooperativeOfficialsQuery,
  useAssignCooperativeOfficialMutation,
  useRemoveCooperativeOfficialMutation,
  useGetMembershipProfileQuery,
  useGetSingleCooperativeDetailsQuery,
  useFetchSingleCoopApprovalsSummaryQuery,
  useUploadCooperativeByeLawsMutation,
  useUploadCooperativeCertificateMutation,
  useGetMembershipPaymentInfoMutation,
  useJoinCooperativeMutation,
  useVerifyMembershipPaymentInfoMutation,
  useGetUserCooperativesQuery,
} = cooperativeApi;
